import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useMatch } from 'react-router-dom';

import Content from 'components/Content';
import FloatingResourceAddButton from 'components/FloatingResourceAddButton';
import Grid from 'components/Grid';
import Header from 'components/Header';
import Navigation from 'components/Navigation';
import Toast from 'components/Toast';
import FloatingResourceAddProvider from 'components/providers/FloatingResourceAddProvider';

import useDevice from 'hooks/useDevice';
import { context as navigationCollapseContext } from 'hooks/useNavigationCollapse';

import DataPage from 'pages/DataPage';
import HomePage from 'pages/HomePage';

import { DESKTOP, TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const Layout: FunctionComponent<Props> = ({ testId = TEST_ID }) => {
    const [isNavigationOpen, setIsNavigationOpen] = useState(true);
    const [isCollapsedForDrawing, setIsCollapsedForDrawing] = useState(false);

    const { devices } = useDevice();

    const isDataRoute = useMatch('/data/:section?');
    const isChemicalsRoute = useMatch('/chemicals/*');
    const isAirspaceRoute = useMatch('/air');
    const isMaintenanceRoute = useMatch('/maintenance');
    const isMobile = devices.deviceType !== DESKTOP;
    const closedStyles = isNavigationOpen ? '' : styles.closed;

    useEffect(() => {
        if (devices.deviceType === DESKTOP) {
            setIsNavigationOpen(true);
        }
    }, [devices.deviceType]);

    const location = useLocation();

    const currentPath = location.pathname;

    const showOutlet = currentPath !== '/' && !isDataRoute;
    const showDataPageClass = isDataRoute ? styles.showDataPage : '';
    const showHomePageClass = currentPath === '/' ? styles.showHomePage : '';

    const navigationOpenRef = useRef(isNavigationOpen);

    navigationOpenRef.current = isNavigationOpen;

    useEffect(() => {
        if (!isMobile || !isCollapsedForDrawing) {
            return;
        }

        const wasOpen = navigationOpenRef.current;

        setIsNavigationOpen(false);

        return () => {
            setIsNavigationOpen(wasOpen);
        };
    }, [isMobile, isCollapsedForDrawing]);

    return (
        <navigationCollapseContext.Provider
            value={{ collapsed: isCollapsedForDrawing, setCollapsed: setIsCollapsedForDrawing }}>
            <FloatingResourceAddProvider>
                <Grid className={`${closedStyles} ${styles.layout}`} data-testid={testId}>
                    <Header
                        className={styles.header}
                        isOpen={isNavigationOpen}
                        onClick={() => setIsNavigationOpen(!isNavigationOpen)}
                    />

                    <Navigation className={styles.navigation} isOpen={isNavigationOpen} />

                    <Content className={styles.content}>
                        <HomePage className={`${showHomePageClass} ${styles.homePage}`} />

                        <DataPage className={`${showDataPageClass} ${styles.dataPage}`} />

                        {showOutlet && <Outlet />}
                    </Content>

                    {!isChemicalsRoute && !isAirspaceRoute && !isMaintenanceRoute && (
                        <FloatingResourceAddButton />
                    )}

                    <Toast />
                </Grid>
            </FloatingResourceAddProvider>
        </navigationCollapseContext.Provider>
    );
};

export default Layout;
